import React, { Component, ReactNode } from 'react';
import { Button, Col, Form, Modal } from 'react-bootstrap';
import { locale } from '../../../common/localization/localizationService';
import moment from 'moment';
import './newOrderModal.scss';
import { IContractor, IRoute } from '../../../models';
import { IAuthState } from '../../../redux/modules/auth';
import { isNil, uniqueId } from 'lodash';
import { AsyncAction } from '../../../utils';
import { Repetition, daylist, getFrequency } from './newOrderConstants';
import { ICustomerGroup } from '../../../services/groupService';
import { IFraction } from '../../../models/Fraction.model';
import { History } from 'history';
import { Formik } from 'formik';
import { DatePicker, Select } from '../../shared/formikFields';
import { debounceEventHandler } from '../../shared/submitFormUtility';
import OrderService from '../ordersService';

import yup from '../../../common/validation';
import { getCustomerIdInUse } from '../../../utils/GeneralUtils';
import { Toastr } from '../../../utils/Toastr';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import ToggleButton from '../../shared/customInputTypes/ToggleButton';
import { Spinner2 } from '../../shared/spinner2';

export interface StoredDynamicExclusion {
  userName: string;
  isDynamic: boolean;
}

export interface INewOrderDialogProps {
  show: boolean;
  contractors?: any;
  routes?: IRoute[];
  authentication?: IAuthState;
  customerGroups?: ICustomerGroup[];
  fractions?: IFraction[];
  match?: any;
  history?: History;
  onHide: (rerender: boolean) => void;
  getContractors?: (
    customerId: number,
    ignoreCache?: boolean
  ) => Promise<Record<string, any>>;
  loadRoutes?: (ignoreCache?: boolean) => AsyncAction;
  loadFractions?: (ignoraCache?: boolean) => AsyncAction;
  orderToUpdate?: any;
}

export interface INewOrderDialogState {
  isOrdinaryDateVisible: boolean;
  isExpireDateVisible: boolean;
  isRecurrentOrderSelected: boolean;
  routes: IRoute;
  contractors: IContractor[];
  fractions: IFraction[];
  customerGroups: ICustomerGroup[];
  emptyDays: Repetition[];
  frequency: Repetition;
  dynamic: boolean;
  warning: boolean;
  isLoading: boolean;
}

interface SelectOption {
  label: string;
  value: any;
}

export default class NewOrderDialog extends Component<INewOrderDialogProps> {
  public getIsDynamicOrder = (): Array<StoredDynamicExclusion> => {
    const isDynamicViews = localStorage.getItem('isDynamicOrder');
    if (isDynamicViews) {
      const isDynamicViewsArray: Array<StoredDynamicExclusion> =
        JSON.parse(isDynamicViews);
      return Array.isArray(isDynamicViewsArray) ? isDynamicViewsArray : [];
    } else {
      return [];
    }
  };

  public setDefaultIsDynamicOrder = (
    userName: string,
    isDynamic: boolean
  ): void => {
    const storedViews = this.getIsDynamicOrder();
    if (storedViews.length) {
      const userIndex = storedViews.findIndex((e) => e.userName === userName);
      if (storedViews[userIndex]) {
        storedViews[userIndex].isDynamic = isDynamic;
      } else {
        storedViews.push({ userName, isDynamic });
      }
    } else {
      storedViews.push({ userName, isDynamic });
    }
    localStorage.setItem('isDynamicOrder', JSON.stringify(storedViews));
  };
  public getDefaultIsDynamicOrder = (): boolean => {
    const userName = localStorage.getItem('userName');
    const storedViews = this.getIsDynamicOrder();
    if (storedViews.length) {
      const userIndex = storedViews.findIndex((e) => e.userName === userName);
      if (storedViews[userIndex]) {
        return storedViews[userIndex].isDynamic;
      }
    }
    return false;
  };

  public readonly state: INewOrderDialogState = {
    isOrdinaryDateVisible: true,
    isExpireDateVisible: true,
    isRecurrentOrderSelected: false,
    routes: null,
    contractors: [],
    customerGroups: [],
    emptyDays: [],
    fractions: [],
    frequency: null,
    dynamic: this.getDefaultIsDynamicOrder(),
    warning: false,
    isLoading: false,
  };

  public componentDidMount(): void {
    const customerId = getCustomerIdInUse(this.props.authentication);
    this.loadContractors(customerId);
    this.props.loadRoutes();
    this.props.loadFractions();
  }

  public getSchema(isRecurrentOrderSelected: boolean): any {
    const isNew = this.props.orderToUpdate?.contractorId ? false : true;

    return yup.object().shape({
      date: yup.date().required().label(locale.orders._startDate),
      ordinarydate: isRecurrentOrderSelected
        ? yup.date().notRequired().nullable().label(locale.orders._ordinaryDate)
        : yup
        .date()
        .required()
        .min(yup.ref('date'))
        .label(locale.orders._ordinaryDate),
      expireDate: isRecurrentOrderSelected
        ? yup.date().notRequired().nullable().label(locale.orders._expireDate)
        : yup
            .date()
            .required()
            .min(yup.ref('date'))
            .min(yup.ref('ordinarydate'))
            .label(locale.orders._expireDate),
      repetationEndDate: isRecurrentOrderSelected
        ? yup
            .date()
            .required()
            .min(yup.ref('date'))
            .label(locale.orders._repetationEndDate)
        : yup
            .date()
            .notRequired()
            .nullable()
            .label(locale.orders._repetationEndDate),
      contractors: yup.object().required().label(locale.orders._contractor),
      routes: isNew
        ? yup.array().required().label(locale.orders._route)
        : yup.object().required().label(locale.orders._route),
      fractions: yup.array().required().label(locale.orders._fractions),
      customerGroups: yup
        .array()
        .required()
        .label(locale.orders._customerGroup),
    });
  }

  private setToNoon = (d: Date) => {
    if (!d) return null;
    const date = new Date(d);
    if (isNaN(date.getTime())) return null;
    date.setHours(12, 0, 0, 0);
    return date;
  };
  private createDtoFromFormData(form: any) {
    const contractor = {
      name: form.contractors?.value?.contractorName,
      contractorId: form.contractors?.value?.contractorId,
    };

    const routes =
      !form.routes || !Array.isArray(form.routes)
        ? []
        : form.routes
            .filter((x: any) => x.value)
            .map((x: any) => ({
              name: x.value.name,
              paSystem: x.value.paSystem,
              routeNumber: x.value.routeNumbder,
            }));

    const fractions =
      !form.fractions || !Array.isArray(form.fractions)
        ? []
        : form.fractions
            .filter((x: any) => x.value)
            .map((x: any) => ({
              name: x.value.name,
              fractionId: x.value.fractionId,
            }));

    const customerTypes =
      !form.customerGroups || !Array.isArray(form.customerGroups)
        ? []
        : form.customerGroups
            .filter((x: any) => x.value)
            .map((x: any) => x.value.customerTypeId);

    const frequency = form.frequency?.value ?? 1;
    const dayList =
      !form.dayList || !Array.isArray(form.dayList)
        ? []
        : form.dayList.filter((x: any) => x.value).map((x: any) => x.value);

    const ordinaryDate = this.state.isOrdinaryDateVisible
      ? form.ordinarydate
      : form.date;
    const expireDate = this.state.isExpireDateVisible
      ? form.expireDate
      : form.date;

    const isDynamic =
      this.state.dynamic && this.state.isRecurrentOrderSelected ? true : false;

    const dto = {
      date: this.setToNoon(form.date),
      ordinaryDate: this.setToNoon(ordinaryDate),
      expireDate: this.setToNoon(expireDate),
      expires: this.setToNoon(expireDate),
      repetitionEndDate: this.setToNoon(form.repetationEndDate),
      contractor: contractor,
      routes: routes,
      fractions: fractions,
      frequency: frequency,
      dayList: dayList,
      customerTypes: customerTypes,
      orderId: 0,
      contractorId: contractor.contractorId,
      route: form?.routes?.value?.routeNumbder,
      dynamic: isDynamic,
    };

    return dto;
  }

  private getInitialValuesToUpdate(
    order: any,
    customerGroupOptions: any,
    fractionOptions: any
  ) {
    if (!order) return null;
    const customerTypeIds =
      order.orderCustomerTypes?.map(
        (customerType: any) => customerType.customerTypeId
      ) || [];

    const fractionIds =
      order.fractions?.map((fraction: any) => fraction.fractionId) || [];

    const initialValuesToUpdate = {
      date: new Date(order.date),
      ordinaryDate: new Date(order.ordinaryDate),
      expireDate: new Date(order.expires),
      contractors: this.getOptions(
        this.props.contractors[this.props.authentication.customerId],
        'contractorName'
      ).filter((c) => c.value.contractorId === order.contractorId)[0],
      routes: this.getOptions(this.props.routes, 'name').find(
        (r) => r.value.name === order.routeData.name
      ),
      fractions: fractionOptions.filter((f: any) =>
        fractionIds.includes(f.value.fractionId)
      ),
      customerGroups: customerGroupOptions.filter((c: any) =>
        customerTypeIds.includes(c.value.customerTypeId)
      ),
    };
    return initialValuesToUpdate;
  }

  private frequencyChange = (name: any, option: any) => {
    if (option.value > 1) {
      this.setState({
        ...this.state,
        isOrdinaryDateVisible: false,
        isExpireDateVisible: false,
        isRecurrentOrderSelected: true,
      });
    } else {
      this.setState({
        ...this.state,
        isOrdinaryDateVisible: true,
        isExpireDateVisible: true,
        isRecurrentOrderSelected: false,
      });
    }
  };

  private loadContractors = async (
    customerId: number,
    ignoreCache = false
  ): Promise<void> => {
    if (!isNil(this.props.authentication.contractorId))
      return new Promise((resolve) => resolve(null));
    const contractorsReq = await this.props.getContractors(
      customerId,
      ignoreCache
    );
    const contractors: Array<IContractor> = contractorsReq.contractors
      ? contractorsReq.contractors.sort((a: IContractor, b: IContractor) =>
          a.contractorName.localeCompare(b.contractorName)
        )
      : [];
    const selectedContractorId =
      (Array.isArray(contractors) && contractors[0]?.contractorId) || -1;

    await new Promise((resolve) => {
      this.setState(
        {
          ...this.state,
          contractorsList: contractors,
          selectedContractorId,
        },
        () => resolve(null)
      );
    });
  };

  public getFilterDate(): moment.Moment {
    const parsedDate = moment(new Date(), 'YYYY-MM-DD', true);

    return parsedDate.isValid() ? parsedDate : moment();
  }

  public getOptions<T>(items: T[], labelProperty: keyof T): SelectOption[] {
    if (items) {
      const options = items.map((item) => {
        return {
          label: item[labelProperty].toString(),
          value: item,
          key: uniqueId(),
        };
      });
      return options;
    }
    return [];
  }

  public onDynamicChange = (): void => {
    const isWarningVisible = !this.state.dynamic ? true : false;
    this.setState({
      ...this.state,
      dynamic: !this.state.dynamic,
      warning: isWarningVisible,
    });
    const userName = localStorage.getItem('userName');
    this.setDefaultIsDynamicOrder(userName, !this.state.dynamic);
  };

  public render(): ReactNode {
    const { show, authentication, orderToUpdate } = this.props;
    const isNew = orderToUpdate?.contractorId ? false : true;
    const customerGroupOptions = this.getOptions(
      this.props.customerGroups,
      'description'
    );
    const fractionOptions = this.getOptions(this.props.fractions, 'name');
    const onHide = this.props.onHide;
    const routeOptions = this.getOptions(this.props.routes, 'name');
    const customerId = getCustomerIdInUse(authentication);
    const contractorOptions = this.getOptions(
      this.props.contractors[customerId],
      'contractorName'
    );
    const date =
      !isNew && orderToUpdate.date
        ? this.setToNoon(new Date(orderToUpdate.date))
        : this.setToNoon(new Date());

    const ordinarydate =
      !isNew && orderToUpdate.ordinaryDate
        ? this.setToNoon(new Date(orderToUpdate.ordinaryDate))
        : this.setToNoon(new Date());

    const expireDate =
      !isNew && orderToUpdate.expires
        ? this.setToNoon(new Date(orderToUpdate.expires))
        : this.setToNoon(new Date());

    const repetationEndDate =
      !isNew && orderToUpdate.repetationEndDate
        ? this.setToNoon(new Date(orderToUpdate.repetationEndDate))
        : this.setToNoon(new Date());

    const frequency = getFrequency();
    const handleSubmit = async (values: any, formikHelpers: any) => {
      try {
        this.setState({ ...this.state, isLoading: true });
        formikHelpers.setSubmitting(true);
        const dto = this.createDtoFromFormData(values);
        if (!isNew) {
          dto.orderId = orderToUpdate.orderId;
          await OrderService.updateOrder(dto);
          Toastr.success(locale.orders._updateSuccess);
        } else {
          await OrderService.createOrder(dto);
        }
        onHide(true);
      } catch (error) {
        console.error('Error submitting form', error);
      } finally {
        this.setState({ ...this.state, isLoading: false });
        formikHelpers.setSubmitting(false);
      }
    };
    const headerText = isNew
      ? locale.orders._newOrder
      : orderToUpdate.routeData.name;
    const submitText = isNew
      ? locale.orders._addOrderButton
      : locale.orders._saveOrderButton;
    const initialValues = !isNew
      ? this.getInitialValuesToUpdate(
          orderToUpdate,
          customerGroupOptions,
          fractionOptions
        )
      : null;
    const isStarted = isNew ? false : orderToUpdate.orderStatus.orderStatusId > 0;
    return (
      <Formik
        validationSchema={() =>
          this.getSchema(this.state.isRecurrentOrderSelected)
        }
        onSubmit={handleSubmit}
        initialValues={{
          ...initialValues,
          date,
          ordinarydate,
          expireDate,
          repetationEndDate,
          frequency: frequency[0],
        }}
      >
        {({ handleSubmit, isValid, dirty }) => (
          <Modal
            onHide={() => onHide(false)}
            show={show}
            centered
            dialogClassName="add-order-modal"
            backdrop="static"
          >
            <Spinner2 show={this.state.isLoading} />
            <Modal.Header closeButton>
              <Modal.Title>{headerText}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form noValidate onSubmit={handleSubmit}>
                <div className="selectors-wrapper">
                  <Select
                    name="contractors"
                    options={contractorOptions}
                    label={locale.orders._contractor}
                    noOptionsMessage={''}
                    loadingMessage={''}
                    placeholder={locale.orders._selectContractor}
                    isLabelTop={true}
                    labelCol={0}
                  />
                  <Select
                    options={routeOptions}
                    placeholder={locale.orders._route}
                    label={locale.orders._selectRoute}
                    name="routes"
                    noOptionsMessage={''}
                    loadingMessage={''}
                    isLabelTop={true}
                    labelCol={0}
                    isMulti={orderToUpdate?.contractorId ? false : true}
                  />
                  <Select
                    options={fractionOptions}
                    placeholder={locale.orders._selectFractions}
                    label={locale.orders._fractions}
                    name="fractions"
                    noOptionsMessage={''}
                    loadingMessage={''}
                    isLabelTop={true}
                    labelCol={0}
                    isMulti={true}
                  />
                  <Select
                    options={customerGroupOptions}
                    placeholder={locale.orders._selectCustomerGroup}
                    label={locale.orders._customerGroup}
                    name="customerGroups"
                    noOptionsMessage={''}
                    loadingMessage={''}
                    isLabelTop={true}
                    labelCol={0}
                    isMulti={true}
                  />
                </div>

                {isNew && (
                  <div className="repetition-input-wrapper">
                    <Col sm={6}>
                      <Select
                        options={frequency}
                        placeholder={locale.orders._selectFrequency}
                        label={locale.orders._frequency}
                        name="frequency"
                        noOptionsMessage={''}
                        loadingMessage={''}
                        isLabelTop={true}
                        labelCol={0}
                        onChange={(name, option) =>
                          this.frequencyChange(name, option)
                        }
                      />
                    </Col>
                    <Col sm={6}>
                      {this.state.isRecurrentOrderSelected && (
                        <Select
                          options={daylist}
                          placeholder={locale.orders._selectDays}
                          label={locale.orders._days}
                          name="dayList"
                          noOptionsMessage={''}
                          loadingMessage={''}
                          isLabelTop={true}
                          labelCol={0}
                          isMulti={true}
                        />
                      )}
                    </Col>
                  </div>
                )}

                <div className="date-input-wrapper">
                  <DatePicker
                    label={locale.orders._startDate}
                    name="date"
                    popupClass="date-picker-popup-above-modal"
                    isLabelTop={true}
                    labelCol={0}
                    col={true}
                    className="order-date-picker"
                    disabled={isStarted}
                  />
                  {this.state.isOrdinaryDateVisible && (
                    <DatePicker
                      label={locale.orders._ordinaryDate}
                      name="ordinarydate"
                      popupClass="date-picker-popup-above-modal"
                      isLabelTop={true}
                      labelCol={0}
                      col={true}
                      className="order-date-picker"
                    />
                  )}
                  {this.state.isExpireDateVisible && (
                    <DatePicker
                      label={locale.orders._expireDate}
                      name="expireDate"
                      popupClass="date-picker-popup-above-modal"
                      isLabelTop={true}
                      labelCol={0}
                      col={true}
                      className="order-date-picker"
                    />
                  )}
                  {this.state.isRecurrentOrderSelected && (
                    <DatePicker
                      label={locale.orders._expireDate}
                      name="repetationEndDate"
                      popupClass="date-picker-popup-above-modal"
                      isLabelTop={true}
                      labelCol={0}
                      col={true}
                    />
                  )}
                </div>
                {isNew && this.state.isRecurrentOrderSelected && (
                  <div className="dynamic-order">
                    <Col sm={4}>
                      <div>{locale.orders._dynamic}</div>
                    </Col>
                    <Col sm={8}>
                      <ToggleButton
                        onToggle={this.onDynamicChange}
                        value={this.state.dynamic}
                      />
                    </Col>
                  </div>
                )}

                <NotificationGroup style={{ position: 'relative' }}>
                  {this.state.dynamic && this.state.warning && (
                    <Notification
                      type={{ style: 'info', icon: true }}
                      closable={true}
                      onClose={() =>
                        this.setState({ ...this.state, warning: false })
                      }
                    >
                      <div className="dynamic-warning-message">
                        <span>{locale.orders._dynamicOrderWarning}</span>
                        <a
                          href="/administration?page=exclusion-days"
                          target="_blank"
                        >
                          {locale.orders._dynamicOrderWarningLink}
                        </a>
                      </div>
                    </Notification>
                  )}
                </NotificationGroup>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="light" onClick={() => onHide(false)}>
                {locale.routeOptimalizationWizard.addCustomerModal._closeButton}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={!isValid || !dirty}
                onClick={debounceEventHandler(handleSubmit, 250)}
              >
                {submitText}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>
    );
  }
}
